import { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogTitle } from '@material-ui/core';
import MuiDialogActions from '@material-ui/core/DialogActions';
import { TextField, Box } from '@mui/material';
import { useMutation } from '@apollo/client';
import {
  APPROVE_PACKAGE_CHANGE,
  REJECT_PACKAGE_CHANGE,
  GET_PENDING_PACKAGE_CHANGES,
} from '../../../Querys/prestaciones/querys';
import { SolicitudCambioPaquete } from '../../../interfaces/Prestaciones.interface';
import { PACKAGE_APPROVE_ACTION, PackageApproveAction } from './helpers/packageRequestStatus';
import { SuccessfulAlert } from '../../../alerts/successAlerts';
import { ErrorAlert } from '../../../alerts/errorAlert';
import { WarningAlert } from '../../../alerts/WarningAlert';
import '../../Collaborator/History/History.css';

interface AprobarSolicitudPaqueteModalProps {
  open: boolean;
  onClose: () => void;
  solicitud: SolicitudCambioPaquete | null;
  action: PackageApproveAction;
  idAprobador: string;
}

const AprobarSolicitudPaqueteModal = ({
  open,
  onClose,
  solicitud,
  action,
  idAprobador,
}: AprobarSolicitudPaqueteModalProps) => {
  const isReject = action === PACKAGE_APPROVE_ACTION.REJECT;
  const [comentario, setComentario] = useState('');
  const [touched, setTouched] = useState(false);

  useEffect(() => {
    setComentario('');
    setTouched(false);
  }, [action, solicitud]);

  const [approve, { loading: loadingApprove }] = useMutation(APPROVE_PACKAGE_CHANGE, {
    refetchQueries: [{ query: GET_PENDING_PACKAGE_CHANGES }],
  });
  const [reject, { loading: loadingReject }] = useMutation(REJECT_PACKAGE_CHANGE, {
    refetchQueries: [{ query: GET_PENDING_PACKAGE_CHANGES }],
  });

  const saving = loadingApprove || loadingReject;
  const motivoInvalido = isReject && comentario.trim() === '';

  const handleConfirm = async () => {
    if (!solicitud) return;
    setTouched(true);
    if (motivoInvalido) return;
    try {
      if (isReject) {
        await reject({ variables: { id: solicitud.id, idAprobador, motivo: comentario.trim() } });
        SuccessfulAlert({ text: 'Solicitud rechazada.' });
      } else {
        await approve({
          variables: { id: solicitud.id, idAprobador, comentario: comentario.trim() || null },
        });
        SuccessfulAlert({ text: 'Solicitud aprobada. El nuevo paquete se aplicó al colaborador.' });
      }
      onClose();
    } catch (e: any) {
      if (e?.message?.includes('REQUEST_NOT_FOUND_OR_NOT_PENDING')) {
        WarningAlert({ text: 'Esta solicitud ya fue procesada por otro aprobador. La bandeja se actualizará.' });
        onClose();
      } else {
        ErrorAlert({ text: 'Ocurrió un error al procesar la solicitud. Intenta de nuevo más tarde.' });
      }
    }
  };

  return (
    <Dialog
      aria-labelledby="aprobar-solicitud-paquete-dialog"
      className="history-modal"
      open={open}
      fullWidth
      maxWidth="sm"
    >
      <DialogTitle>
        <h2 id="form-dialog-title" className="text-center" style={{ marginTop: 20 }}>
          {isReject ? 'Rechazar solicitud' : 'Aprobar solicitud'}
        </h2>
        <span id="closemodalx" onClick={onClose}>
          <img alt="close" src="/assets/svg/CloseM.svg" />
        </span>
      </DialogTitle>

      <DialogContent>
        {solicitud && (
          <Box mb={2} mt={1} style={{ fontFamily: 'Roboto', fontSize: 14, color: '#444' }}>
            <p><strong>Colaborador:</strong> {solicitud.nombreColaborador ?? solicitud.idColaborador}</p>
            <p><strong>Paquete actual:</strong> {solicitud.nombrePaqueteAnterior ?? 'Sin paquete'}</p>
            <p><strong>Paquete solicitado:</strong> {solicitud.nombrePaqueteNuevo ?? solicitud.idPaqueteNuevo}</p>
            <p><strong>Solicitado por:</strong> {solicitud.nombreSolicitante ?? solicitud.idSolicitante}</p>
            <p><strong>Motivo:</strong> {solicitud.motivo}</p>
          </Box>
        )}

        <Box mb={1}>
          <TextField
            label={isReject ? 'Motivo de rechazo' : 'Comentario (opcional)'}
            value={comentario}
            onChange={(e) => setComentario(e.target.value)}
            variant="outlined"
            size="small"
            fullWidth
            multiline
            minRows={3}
            error={touched && motivoInvalido}
            helperText={touched && motivoInvalido ? 'El motivo de rechazo es obligatorio' : ''}
          />
        </Box>
      </DialogContent>

      <MuiDialogActions>
        <button className="btnCancel" onClick={onClose} disabled={saving}>
          Cancelar
        </button>
        <button
          onClick={handleConfirm}
          disabled={saving}
          style={{
            backgroundColor: isReject ? '#e53935' : '#11ca73',
            color: '#fff',
            border: 'none',
            borderRadius: 8,
            padding: '8px 20px',
            fontFamily: 'Roboto',
            fontWeight: 600,
            cursor: saving ? 'default' : 'pointer',
            opacity: saving ? 0.6 : 1,
          }}
        >
          {saving ? 'Procesando…' : isReject ? 'Rechazar' : 'Aprobar'}
        </button>
      </MuiDialogActions>
    </Dialog>
  );
};

export default AprobarSolicitudPaqueteModal;
